import { afterNextRender, Component, computed, effect, ElementRef, inject, Injector, signal, viewChild } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastService } from '../../ui';
import { MatDialog } from '@angular/material/dialog';
import { SessionService } from '../../core/session/session.service';
import { BillingStatusGuardService } from '../../shared/billing-status/billing-status-guard.service';
import { InvoiceService, CustomerSettings, Invoice, Language, BillingDetails } from './invoice.service';
import { BoolDisplayPipe } from '../../shared/pipes/bool-display.pipe';
import { EvnPdfDialogComponent } from './dialogs/evn-pdf-dialog.component';
import { EvnExcelDialogComponent } from './dialogs/evn-excel-dialog.component';
import { DetailPdfDialogComponent } from './dialogs/detail-pdf-dialog.component';
import { CustomerBillingComponent } from './customer-billing/customer-billing.component';
import { UsageCreditComponent } from './usage-credit/usage-credit.component';
import { RevealOnExpandDirective } from '../../shared/directives/reveal-on-expand.directive';
import {
  GenerateInvoiceDialogComponent,
  GenerateInvoiceResult,
} from './generate-invoice-dialog/generate-invoice-dialog.component';
import { forkJoin } from 'rxjs';
import {
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmNativeSelectDirective,
  HlmBadgeComponent,
  HlmSpinnerComponent,
  HlmTooltipDirective,
  HLM_FORM_FIELD,
} from '../../ui';

@Component({
  selector: 'cmt-invoice',
  imports: [
    ReactiveFormsModule,
    DatePipe,
    DecimalPipe,
    BoolDisplayPipe,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmNativeSelectDirective,
    HlmBadgeComponent,
    HlmSpinnerComponent,
    HlmTooltipDirective,
    ...HLM_FORM_FIELD,
    CustomerBillingComponent,
    UsageCreditComponent,
    RevealOnExpandDirective,
  ],
  // GenerateInvoiceDialogComponent is opened via MatDialog (not in the template),
  // so it is NOT listed in `imports`; it is referenced directly in openManualInvoiceDialog().
  templateUrl: './invoice.component.html',
})
export class InvoiceComponent {
  readonly sessionService = inject(SessionService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly toast = inject(ToastService);
  private readonly injector = inject(Injector);
  private readonly dialog = inject(MatDialog);
  private readonly billingStatusGuardService = inject(BillingStatusGuardService);

  /** Whether the current user may edit the billing-status flag (EB-3820, session-stable). */
  readonly canEditBilling = this.billingStatusGuardService.canEdit();

  /** Snapshot of billingEnabled captured on edit, so confirmChange sees the true previous value. */
  private originalBillingEnabled: boolean | null = null;

  readonly editMode = signal(false);
  readonly saving = signal(false);
  readonly loadingInvoices = signal(true);
  readonly loadingBillingDetails = signal(true);
  readonly settings = signal<CustomerSettings | null>(null);
  readonly billingDetails = signal<BillingDetails | null>(null);
  readonly languages = signal<Language[]>([]);
  readonly invoices = signal<Invoice[]>([]);
  readonly sortedInvoices = signal<Invoice[]>([]);
  readonly searchQuery = signal('');
  readonly invoicesSectionRef = viewChild<ElementRef<HTMLElement>>('invoicesSection');

  /**
   * Wired from cmt-customer-billing's `(jumpToInvoice)` output. The billing
   * row carries the *internal* invoice.id; the Invoices table filter searches
   * the official `showReId`. Translate before pre-filling so the user lands
   * on the correct row. Falls back to the internal id when the lookup fails
   * (e.g. invoice still loading) — the search will simply yield no match.
   */
  /**
   * Customer-billing back-fills a missing invoice via /invoice/v1/invoices/{id}
   * (the legacy /contract/v2 list excludes recently-created, not-yet-printed
   * invoices). Append the record to our `invoices()` signal so it shows up
   * in the Invoices table AND so the badge resolver picks up the showReId.
   * No-op on duplicate id (signal is idempotent if the record already exists).
   */
  onInvoiceResolved(invoice: Invoice): void {
    this.invoices.update((list) =>
      list.some((i) => i.id === invoice.id)
        ? list
        : [invoice, ...list].sort((a, b) =>
            (b.creationDate || '').localeCompare(a.creationDate || ''),
          ),
    );
  }

  jumpToInvoice(internalInvoiceId: number): void {
    const invoice = this.invoices().find((inv) => inv.id === internalInvoiceId);
    const queryValue = invoice?.showReId ?? internalInvoiceId;
    this.collapsed.update((c) => ({ ...c, invoices: false }));
    this.searchQuery.set(String(queryValue));
    // Defer scroll until Angular has flushed the post-collapse re-render.
    // `afterNextRender` is OnPush-safe — `setTimeout(0)` fires too early
    // and may target the still-collapsed/empty layout.
    afterNextRender(
      () => this.invoicesSectionRef()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      { injector: this.injector },
    );
  }
  private loaded = false;

  /**
   * Map-driven section keys (EB-3883). The Billing section is NOT in this map —
   * `cmt-customer-billing` owns its own collapse state via a two-way `collapsed`
   * model, bound to `billingCollapsed` below. Keeping the map for the two
   * in-page sections (Settings, Invoices) and a separate signal for each child
   * section model keeps Collapse-all uniform without leaking the child's state
   * into the parent map.
   *
   * EXTENSION POINT (usage credit): when `cmt-usage-credit` is mounted, add a
   * `creditCollapsed = signal<boolean>(true)` here (default collapsed), bind it
   * `[(collapsed)]="creditCollapsed"` on the element, and extend `allCollapsed`
   * + `toggleAll` to include it (see the marked spots below).
   */
  readonly sectionKeys = ['settings', 'invoices'] as const;

  /**
   * Collapse state for the map-driven sections.
   * Default: Settings + Invoices EXPANDED (false).
   */
  readonly collapsed = signal<Record<string, boolean>>({ settings: false, invoices: false });

  /**
   * Billing section collapse state (two-way bound to `cmt-customer-billing`).
   * Default: COLLAPSED (true).
   */
  readonly billingCollapsed = signal(true);

  /**
   * Usage Credit section (EB-3883) — self-contained `cmt-usage-credit` card,
   * bound via two-way `collapsed` model. Default COLLAPSED (true), matching the
   * Billing section; only Settings + Invoices open by default.
   */
  readonly creditCollapsed = signal(true);

  readonly allCollapsed = computed(() => {
    const c = this.collapsed();
    const mapCollapsed = this.sectionKeys.every((k) => c[k] === true);
    return mapCollapsed && this.billingCollapsed() && this.creditCollapsed();
  });

  /** Current sort state for the plain HTML table */
  private currentSortActive = 'creationDate';
  private currentSortDirection: 'asc' | 'desc' = 'desc';

  /**
   * Filtered invoices based on search query. Matches against:
   *   - Official invoice number (`showReId`)
   *   - Internal id (so a billing-row "internal {id}" can be pasted)
   *   - Creation date in BOTH the raw ISO form (`2026-04-22`) and the
   *     German display form (`22.04.2026`, `04.2026`, `2026`) so the
   *     user can search the way the column actually reads.
   */
  readonly filteredInvoices = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const data = this.sortedInvoices();
    if (!query) return data;
    return data.filter((inv) => {
      if (String(inv.showReId).includes(query)) return true;
      if (String(inv.id).includes(query)) return true;
      const iso = inv.creationDate ?? '';
      if (iso.toLowerCase().includes(query)) return true;
      // ISO `2026-04-22` → German `22.04.2026`. Substring match also
      // covers month-only ("04.2026") and year-only ("2026") queries.
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
      if (m) {
        const [, yyyy, mm, dd] = m;
        const german = `${dd}.${mm}.${yyyy}`;
        if (german.includes(query)) return true;
        if (`${mm}.${yyyy}`.includes(query)) return true;
      }
      return false;
    });
  });

  /** Edit disabled if customer contract has ended */
  readonly editDisabled = computed(() => !!this.sessionService.customer()?.endDate);

  readonly minDate: Date;
  /** ISO string for native date input min attribute */
  readonly minDateStr: string;

  /** Settings form */
  readonly settingsForm = new FormGroup({
    billingLanguage: new FormControl<number | null>(null),
    invoiceDay: new FormControl<number>(1, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1), Validators.max(31)],
    }),
    paymentTerm: new FormControl<number>(0, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(0)],
    }),
    usageFeeLimit: new FormControl<number>(0, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(0)],
    }),
    // Per PO, "Not Selected" (null) officially never occurs — default to Yes so a
    // real Yes/No option is always selected in edit mode (mirrors customer-information).
    billingEnabled: new FormControl<boolean | null>(true),
  });

  /** Resolved language name for read mode */
  readonly billingLanguageName = computed(() => {
    const s = this.settings();
    const langs = this.languages();
    if (!s || !s.mainCustomerLanguage) return '—';
    const found = langs.find(l => l.id === s.mainCustomerLanguage);
    return found ? found.name : String(s.mainCustomerLanguage);
  });

  constructor() {
    // Old CMT: date-min-limit = today (no past dates allowed).
    // Build the min in LOCAL (Europe/Berlin) time — toISOString() is UTC and
    // would yield "yesterday" in the early hours after local midnight
    // (00:00-02:00 Berlin), when Berlin is already on the next day but UTC isn't.
    this.minDate = new Date();
    const d = this.minDate;
    this.minDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    effect(() => {
      const customerId = this.sessionService.customerId();
      if (customerId !== null && !this.loaded) {
        this.loaded = true;
        this.initData(customerId);
      }
    });
  }

  private initData(customerId: number): void {
    forkJoin({
      settings: this.invoiceService.getSettings(customerId),
      languages: this.invoiceService.getLanguages(),
    }).subscribe({
      next: (result) => {
        this.settings.set(result.settings);
        this.languages.set(result.languages);
      },
      error: () => {
        this.toast.open('Failed to load invoice settings', 'OK', { duration: 5000 });
      },
    });

    this.loadBillingDetails(customerId);
    this.loadInvoices(customerId);
  }

  private loadBillingDetails(customerId: number): void {
    this.loadingBillingDetails.set(true);
    this.invoiceService.getBillingDetails(customerId).subscribe({
      next: (details) => {
        this.billingDetails.set(details);
        this.loadingBillingDetails.set(false);
      },
      error: () => {
        this.loadingBillingDetails.set(false);
        this.toast.open('Failed to load billing details', 'OK', { duration: 5000 });
      },
    });
  }

  refreshBillingDetails(): void {
    const customerId = this.sessionService.customerId();
    if (customerId === null) return;
    this.loadBillingDetails(customerId);
  }

  toggleSection(key: string): void {
    this.collapsed.update((c) => ({ ...c, [key]: !c[key] }));
  }

  isCollapsed(key: string): boolean {
    return this.collapsed()[key] === true;
  }

  toggleAll(): void {
    const shouldCollapse = !this.allCollapsed();
    const next: Record<string, boolean> = {};
    for (const k of this.sectionKeys) next[k] = shouldCollapse;
    this.collapsed.set(next);
    this.billingCollapsed.set(shouldCollapse);
    this.creditCollapsed.set(shouldCollapse);
  }

  /**
   * Open the Manual Invoice generation dialog (EB-3883 — replaces the former
   * page section). On success the dialog closes with a truthy result; refresh
   * the Invoices list + toast (same observable behaviour as the old inline
   * section's generateInvoice()).
   */
  openManualInvoiceDialog(): void {
    const customerId = this.sessionService.customerId();
    if (customerId === null) return;
    const ref = this.dialog.open<
      GenerateInvoiceDialogComponent,
      { customerId: number; minDateStr: string },
      GenerateInvoiceResult | null
    >(GenerateInvoiceDialogComponent, {
      data: { customerId, minDateStr: this.minDateStr },
      width: '100%',
      maxWidth: '560px',
      autoFocus: false,
      restoreFocus: true,
      panelClass: 'cmt-hlm-dialog',
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.generated) {
        this.refreshInvoices();
        this.toast.open('Invoice scheduled for tomorrow morning', 'OK', { duration: 5000 });
      }
    });
  }

  private loadInvoices(customerId: number): void {
    this.loadingInvoices.set(true);
    this.invoiceService.getInvoices(customerId).subscribe({
      next: (invoices) => {
        // Sort by creation date descending by default
        const sorted = [...invoices].sort((a, b) =>
          (b.creationDate ?? '').localeCompare(a.creationDate ?? '')
        );
        this.invoices.set(sorted);
        this.sortedInvoices.set(sorted);
        this.loadingInvoices.set(false);
      },
      error: () => {
        this.loadingInvoices.set(false);
        this.toast.open('Failed to load invoices', 'OK', { duration: 5000 });
      },
    });
  }

  /** Sort handler for plain HTML table column clicks */
  onSortColumn(column: string): void {
    if (this.currentSortActive === column) {
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortActive = column;
      this.currentSortDirection = 'asc';
    }

    const data = [...this.invoices()];
    const dir = this.currentSortDirection === 'asc' ? 1 : -1;
    data.sort((a, b) => {
      let valA: any;
      let valB: any;

      if (column === 'total') {
        valA = (a.basePrice ?? 0) + (a.unitPrice ?? 0);
        valB = (b.basePrice ?? 0) + (b.unitPrice ?? 0);
      } else {
        valA = (a as any)[column];
        valB = (b as any)[column];
      }

      if (typeof valA === 'number' && typeof valB === 'number') {
        return (valA - valB) * dir;
      }
      return String(valA ?? '').localeCompare(String(valB ?? '')) * dir;
    });
    this.sortedInvoices.set(data);
  }

  onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  /** Wipe the Invoices search field — used by the inline X button. */
  clearInvoiceSearch(): void {
    this.searchQuery.set('');
  }

  downloadEvnPdf(invoice: Invoice): void {
    this.dialog.open(EvnPdfDialogComponent, {
      width: '90vw',
      height: '92vh',
      maxWidth: '90vw',
      data: { invoiceId: invoice.id, invoiceNumber: invoice.showReId },
      panelClass: 'cmt-hlm-dialog',
    });
  }

  downloadEvnExcel(invoice: Invoice): void {
    this.dialog.open(EvnExcelDialogComponent, {
      width: '450px',
      data: { invoiceId: invoice.id, invoiceNumber: invoice.showReId },
      panelClass: 'cmt-hlm-dialog',
    });
  }

  downloadDetail(invoice: Invoice): void {
    const allInvoices = this.filteredInvoices();
    const currentIndex = allInvoices.findIndex(inv => inv.id === invoice.id);
    const ref = this.dialog.open(DetailPdfDialogComponent, {
      width: '90vw',
      height: '92vh',
      maxWidth: '90vw',
      data: {
        invoiceId: invoice.id,
        invoiceNumber: invoice.showReId,
        allInvoices,
        currentIndex: currentIndex >= 0 ? currentIndex : 0,
      },
      panelClass: 'cmt-hlm-dialog',
    });
    // Credit / regenerate create a new preliminary invoice — refresh the list behind
    // the dialog right away, as old CMT's `refreshList` does (EB-4396).
    ref.componentInstance.invoicesChanged.subscribe(() => this.refreshInvoices());
  }

  refreshInvoices(): void {
    const customerId = this.sessionService.customerId();
    if (customerId === null) return;
    this.loadInvoices(customerId);
  }

  startEdit(): void {
    const s = this.settings();
    if (!s) return;
    // Coerce a null billingEnabled to true (Yes) so the select never sits on an
    // unselectable null state — "Not Selected" officially never occurs (per PO).
    const billingEnabled = s.billingEnabled ?? true;
    this.settingsForm.patchValue({
      billingLanguage: s.mainCustomerLanguage,
      invoiceDay: s.invoiceDay,
      paymentTerm: s.paymentTerm,
      usageFeeLimit: s.usageFeeLimit,
      billingEnabled,
    });
    // Snapshot the RAW persisted value (no ?? true) so confirmChange sees the true
    // previous state — aligns with customer-information which passes raw c.billingEnabled.
    // The form control keeps the null→true coercion above for the select default.
    this.originalBillingEnabled = s.billingEnabled;
    this.settingsForm.markAsPristine();
    this.editMode.set(true);
  }

  cancelEdit(): void {
    this.settingsForm.reset();
    this.editMode.set(false);
  }

  save(): void {
    const customerId = this.sessionService.customerId();
    if (customerId === null || this.settingsForm.invalid) return;

    // Build changes from dirty controls EXCEPT billingEnabled first.
    const changes: Record<string, any> = {};
    const controls = this.settingsForm.controls;

    if (controls.billingLanguage.dirty) {
      changes['mainCustomerLanguage'] = controls.billingLanguage.value;
    }
    if (controls.invoiceDay.dirty) {
      changes['invoiceDay'] = controls.invoiceDay.value;
    }
    if (controls.paymentTerm.dirty) {
      changes['paymentTerm'] = controls.paymentTerm.value;
    }
    if (controls.usageFeeLimit.dirty) {
      changes['usageFeeLimit'] = controls.usageFeeLimit.value;
    }

    // Belt-and-braces: billingEnabled only ever enters the payload here, inside the
    // confirmed branch, and only when the user is permitted — a non-permitted user
    // can never PATCH it even if the control is somehow dirty (EB-3820).
    const billingDirty = this.canEditBilling && controls.billingEnabled.dirty;
    if (!billingDirty) {
      this.applyPatch(customerId, changes);
      return;
    }

    this.billingStatusGuardService.confirmChange(this.originalBillingEnabled, controls.billingEnabled.value).subscribe(ok => {
      if (!ok) return; // abort, stay in edit, no spinner
      changes['billingEnabled'] = controls.billingEnabled.value;
      this.applyPatch(customerId, changes);
    });
  }

  private applyPatch(customerId: number, changes: Record<string, any>): void {
    if (Object.keys(changes).length === 0) {
      this.editMode.set(false);
      this.saving.set(false);
      return;
    }

    this.saving.set(true);

    this.invoiceService.patchCustomer(customerId, changes).subscribe({
      next: () => {
        // Reload settings to reflect changes
        this.invoiceService.getSettings(customerId).subscribe({
          next: (s) => this.settings.set(s),
        });
        // The session customer carries invoiceDay too (Usage Credit "Set to invoicing day")
        // — refresh it, as other editors of customer data do (code review EB-4396 M4).
        this.sessionService.reloadCustomer();
        this.editMode.set(false);
        this.saving.set(false);
        this.toast.open('Invoice settings saved', 'OK', { duration: 3000 });
      },
      error: () => {
        this.saving.set(false);
        this.toast.open('Failed to save invoice settings', 'OK', { duration: 5000 });
      },
    });
  }

  formatBool(value: boolean | null | undefined): string {
    if (value === null || value === undefined) return 'Not Selected';
    return value ? 'Yes' : 'No';
  }
}
